import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { UserRole } from '@/types';

export interface AuthUser {
  userId: string;
  email: string;
  displayName: string;
  roles: UserRole[];
  departmentScopes: string[];
  isAdmin: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: () => void;
  logout: () => void;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  impersonatingRole: UserRole | null;
  setImpersonatingRole: (role: UserRole | null) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthMeResponse {
  clientPrincipal: {
    identityProvider: string;
    userId: string;
    userDetails: string;
    userRoles: string[];
    claims: Array<{ typ: string; val: string }>;
  } | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impersonatingRole, setImpersonatingRole] = useState<UserRole | null>(null);

  useEffect(() => {
    fetchAuthSession();
  }, []);

  async function fetchAuthSession() {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch('/.auth/me');
      if (!response.ok) {
        throw new Error('Failed to fetch auth session');
      }

      const data: AuthMeResponse = await response.json();

      if (!data.clientPrincipal) {
        setUser(null);
        return;
      }

      const principal = data.clientPrincipal;
      const claims = principal.claims ?? [];

      const getClaim = (type: string): string =>
        claims.find((c) => c.typ === type)?.val ?? '';

      // Extract roles from claims (custom claim populated by Entra ID)
      const roleClaims = claims
        .filter((c) => c.typ === 'roles' || c.typ === 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role')
        .map((c) => c.val);

      // Map Entra role names (e.g., "Nexus-Admin") to app role names (e.g., "Admin")
      const mappedRoleClaims = roleClaims.map((r) =>
        r.startsWith('Nexus-') ? r.replace('Nexus-', '').replace(/-/g, ' ') : r
      );

      const roles = mappedRoleClaims
        .filter((r): r is UserRole => Object.values(UserRole).includes(r as UserRole));

      // Extract department scopes from custom claim
      const scopeClaims = claims
        .filter((c) => c.typ === 'departmentScopes')
        .map((c) => c.val);

      const departmentScopes = scopeClaims.length > 0
        ? scopeClaims.flatMap((s) => s.split(',').map((v) => v.trim()))
        : [];

      const isAdmin = roles.includes(UserRole.Admin);

      setUser({
        userId: principal.userId,
        email: principal.userDetails || getClaim('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'),
        displayName: getClaim('name') || getClaim('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name') || principal.userDetails,
        roles,
        departmentScopes,
        isAdmin,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication error');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  const login = useCallback(() => {
    window.location.href = '/.auth/login/aad';
  }, []);

  const logout = useCallback(() => {
    window.location.href = '/.auth/logout';
  }, []);

  const hasRole = useCallback(
    (role: UserRole) => {
      if (impersonatingRole) return impersonatingRole === role;
      return user?.isAdmin || user?.roles.includes(role) || false;
    },
    [user, impersonatingRole],
  );

  const hasAnyRole = useCallback(
    (roles: UserRole[]) => {
      if (impersonatingRole) return roles.includes(impersonatingRole);
      return user?.isAdmin || roles.some((r) => user?.roles.includes(r)) || false;
    },
    [user, impersonatingRole],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        error,
        login,
        logout,
        hasRole,
        hasAnyRole,
        impersonatingRole,
        setImpersonatingRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
